import { useEffect, useState, type CSSProperties } from 'react'
import type { MilemarkerTeamRow } from '../../hooks/milemarker/useMilemarkerData'
import AppIcon from '../icons/AppIcon'
import TeamStandingRow from './TeamStandingRow'
import styles from './MilemarkerScreen.module.css'

type Offscreen = 'above' | 'below' | null

interface MilemarkerTeamListProps {
  teams: MilemarkerTeamRow[]
  onSelectTeam?: (teamID: string) => void
}

function useOffscreenDirection(element: HTMLElement | null): Offscreen {
  const [direction, setDirection] = useState<Offscreen>(null)

  useEffect(() => {
    if (!element) {
      setDirection(null)
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry || entry.isIntersecting) setDirection(null)
      else setDirection(entry.boundingClientRect.top < window.innerHeight / 2 ? 'above' : 'below')
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return direction
}

export default function MilemarkerTeamList({ teams, onSelectTeam }: MilemarkerTeamListProps) {
  const [userRow, setUserRow] = useState<HTMLButtonElement | null>(null)
  const [flashing, setFlashing] = useState(false)
  const offscreen = useOffscreenDirection(userRow)
  const userTeam = teams.find((team) => team.isUserTeam)
  const goalCount = teams[0]?.breakdown.length ?? 0

  if (teams.length === 0) {
    return <p className={styles.empty}>No teams found</p>
  }

  function jumpToUserTeam() {
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    userRow?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' })
    userRow?.focus({ preventScroll: true })
    setFlashing(true)
  }

  return (
    <>
      <div className={styles.table} style={{ '--goal-count': goalCount } as CSSProperties}>
        <div className={styles.tableHead} aria-hidden>
          <span>#</span>
          <span>Team</span>
          <span>Goals</span>
          <span className={styles.headRms}>RMs</span>
        </div>
        <ol className={styles.rows}>
          {teams.map((team) => (
            <TeamStandingRow
              key={team.id}
              team={team}
              flash={team.isUserTeam && flashing}
              rowRef={team.isUserTeam ? setUserRow : undefined}
              onSelect={
                onSelectTeam && !team.isPaceTeam ? () => onSelectTeam(team.id) : undefined
              }
              onFlashEnd={() => setFlashing(false)}
            />
          ))}
        </ol>
      </div>

      {userTeam && offscreen && (
        <button
          type="button"
          className={styles.jump}
          onClick={jumpToUserTeam}
          aria-label={`Jump to ${userTeam.name}`}
        >
          <span className={styles.jumpArrow}>
            <AppIcon icon={offscreen === 'above' ? 'mdi:arrow-up' : 'mdi:arrow-down'} size={13} />
          </span>
          {userTeam.name}
          <span className={styles.jumpRank}>#{userTeam.rank}</span>
        </button>
      )}
    </>
  )
}
